// BOARD-RESULTS (docs/design/2026-11-02-board-stages-gates-kpis.md §5, §6), behind the company
// flag `settings.flags.board_results` (default off; every route but the results read answers 404
// while it is off, and the read says `enabled: false`). Handlers: services/api
// routes/work/post-results.ts, domain/boards/results.ts and domain/boards/research-signals.ts.
//
// - A published card's results: the post (when it actually went out, the channel, the content
//   type) and its readings at +24 h and +72 h (likes, comments, shares, reach, saves). A person
//   enters them with "Add results" (`source: manual`); a social connector writes the same body
//   later (`source: connector`, `external_ref` its post id). The readings are the task's metric
//   observations (`task_observation`), observed at posted_at + 24 h / + 72 h; entering a window
//   again corrects its readings (supersedes), never duplicates them.
// - Posting times: the learner's recommended slots for a channel × content type in a week
//   (company time, inside the channel's posting window), and whether it is still exploring.
// - Findings: a board's Research cards with their signal fields, urgent first, then by evidence.
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

const Key = z
  .string()
  .max(40)
  .regex(/^[a-z][a-z0-9_]{0,39}$/);
const Count = z.int().min(0).max(1_000_000_000);

export const RESULT_METRICS = ["likes", "comments", "shares", "reach", "saves"] as const;
export const ResultWindow = z.enum(["24h", "72h"]).meta({ id: "ResultWindow" });

export const PostReadings = z
  .object({
    likes: z.number().nullable(),
    comments: z.number().nullable(),
    shares: z.number().nullable(),
    reach: z.number().nullable(),
    saves: z.number().nullable(),
  })
  .meta({ id: "PostReadings" });

export const PostResults = z
  .object({
    /** Whether results are switched on for the company (the flag). */
    enabled: z.boolean(),
    publication: z
      .object({
        channel: z.string().nullable(),
        format: z.string().nullable(),
        posted_at: Timestamp,
        source: z.enum(["estimated", "manual", "connector"]),
        post_link: z.string().nullable(),
      })
      .nullable(),
    readings: z.object({ "24h": PostReadings.nullable(), "72h": PostReadings.nullable() }),
    /** The board's Channel and Format choices (key, name), for the form's pickers. */
    channels: z.array(z.object({ key: z.string(), name: z.string() })),
    formats: z.array(z.object({ key: z.string(), name: z.string() })),
  })
  .meta({ id: "PostResults" });
export type PostResults = z.infer<typeof PostResults>;

export const PostResultsRecord = z
  .strictObject({
    channel: Key,
    format: Key.optional(),
    posted_at: Timestamp.describe("When the post actually went out"),
    window: ResultWindow,
    likes: Count.optional(),
    comments: Count.optional(),
    shares: Count.optional(),
    reach: Count.optional(),
    saves: Count.optional(),
    post_link: z.url().max(2000).optional(),
    source: z.enum(["manual", "connector"]).optional().describe("Default manual"),
    external_ref: z.string().min(1).max(200).optional().describe("The connector's post id"),
  })
  .refine((b) => RESULT_METRICS.some((m) => b[m] !== undefined), {
    message: "give at least one reading",
  })
  .meta({ id: "PostResultsRecord" });

export const PostingTimesQuery = z.object({
  channel: Key,
  format: Key.optional(),
  /** ISO week (`2026-W45`); default the coming week. */
  week: z
    .string()
    .max(8)
    .regex(/^\d{4}-W\d{2}$/)
    .optional(),
  count: z.coerce.number().int().min(1).max(14).optional(),
});

export const PostingTimes = z
  .object({
    channel: z.string(),
    format: z.string(),
    week: z.string(),
    timezone: z.string(),
    window: z.object({ start_hour: z.int(), end_hour: z.int() }),
    mode: z.enum(["explore", "exploit"]),
    /** "exploring: 3 of 5 slots are tests" — the line the weekly plan writes. */
    summary: z.string(),
    /** Posts with readings so far for this channel × format. */
    posts: z.int(),
    slots: z.array(
      z.object({
        weekday: z.int().min(1).max(7),
        hour: z.int().min(0).max(23),
        /** The slot in the week, company time, as an instant. */
        at: Timestamp,
        label: z.string(),
        /** 1.0 = the channel's median post. */
        expected: z.number(),
        n: z.int(),
        explore: z.boolean(),
      }),
    ),
  })
  .meta({ id: "PostingTimes" });
export type PostingTimes = z.infer<typeof PostingTimes>;

export const FindingsQuery = z.object({
  signal: z.enum(["urgent", "trending", "rising", "evergreen"]).optional(),
  status: Key.optional().describe("The column to read (default research)"),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

export const Finding = z
  .object({
    task_id: Uuid,
    title: z.string(),
    status: z.string(),
    signal: z.string().nullable(),
    trend_direction: z.string().nullable(),
    search_interest: z.number().nullable(),
    engagement: z.number().nullable(),
    sources: z.number().nullable(),
    evidence: z.string().nullable(),
    why_it_matters: z.string().nullable(),
    offer_fit: z.string().nullable(),
    expires: z.string().nullable(),
  })
  .meta({ id: "Finding" });
export const Findings = z.object({ data: z.array(Finding) }).meta({ id: "Findings" });

export const ResearchSignalsSetup = z
  .object({
    /** The signal fields the call created (names); the ones the board had are kept. */
    created_fields: z.array(z.string()),
    /** The "Expires → Parked" rule (created now or already there); null without those columns. */
    rule_id: Uuid.nullable(),
  })
  .meta({ id: "ResearchSignalsSetup" });

const BoardParams = CompanyItemParams;

export const postResultsRoutes = {
  "tasks.results.get": defineRoute({
    ...core,
    id: "tasks.results.get",
    method: "get",
    path: `${base}/tasks/{id}/results`,
    tag: "tasks",
    summary: "A published card's post and its 24 h / 72 h readings",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: PostResults },
    errors: [],
    emits: [],
  }),
  "tasks.results.record": defineRoute({
    ...core,
    id: "tasks.results.record",
    method: "post",
    path: `${base}/tasks/{id}/results`,
    tag: "tasks",
    summary: "Add a published card's results for one window (a person, or a connector later)",
    access: "member",
    params: CompanyItemParams,
    body: PostResultsRecord,
    response: { status: 200, schema: PostResults },
    errors: ["not_found", "conflict"],
    emits: ["task.observation_recorded", "task.field_changed"],
  }),
  "postingTimes.recommend": defineRoute({
    ...core,
    id: "postingTimes.recommend",
    method: "get",
    path: `${base}/posting-times`,
    tag: "tasks",
    summary: "The recommended posting slots for a channel and content type in a week",
    access: "member",
    params: CompanyParams,
    query: PostingTimesQuery,
    response: { status: 200, schema: PostingTimes },
    errors: ["not_found"],
    emits: [],
  }),
  "boards.findings": defineRoute({
    ...core,
    id: "boards.findings",
    method: "get",
    path: `${base}/boards/{id}/findings`,
    tag: "boards",
    summary: "A board's research findings with their signal, urgent first, then by evidence",
    access: "member",
    params: BoardParams,
    query: FindingsQuery,
    response: { status: 200, schema: Findings },
    errors: ["not_found"],
    emits: [],
  }),
  "boards.researchSignals.setup": defineRoute({
    ...core,
    id: "boards.researchSignals.setup",
    method: "post",
    path: `${base}/boards/{id}/research-signals`,
    tag: "boards",
    summary: "Give a board the research signal fields and the Expires → Parked rule",
    access: "member",
    params: BoardParams,
    response: { status: 200, schema: ResearchSignalsSetup },
    errors: ["not_found", "forbidden"],
    emits: ["field_def.created", "automation.created"],
  }),
} as const;
